import React, { useCallback, useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import SelectInputCachedFormik from "../../../components/UI/SelectInput/SelectInputCachedFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import HtmlEditor from "../../../components/UI/HtmlEditorInput/HtmlEditor";
import SelectInputTipoParteServicoFormik from "../../../components/UI/SelectInput/SelectInputTipoParteServicoFormik";

const validationSchema = Yup.object().shape({
  sqTextoParteServico: Yup.string().default(),
  sqTipoParteServico: Yup.string().default(),
  sqTipoInaptidao: Yup.string().default(),
  txTextoPadrao: Yup.string().ensure().default("").required("Informe o Texto de Parte de Serviço"),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const FormTextoParteServico = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [selectInaptidao, setSelectInaptidao] = useState("");
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sqTextoParteServico)
        requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraTextoParteServico, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.incluiTextoParteServico, values);
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Texto de Parte de Serviço salvo com sucesso");
      history.push("/app/textoparteservico");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const {
    request: textoParteServicoRequest,
    data: textoParteServicoData,
    errors: textoParteServicoErrors,
    isLoading: textoParteServicoIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      textoParteServicoRequest(apiRoutes.fiscalizacao.parametrizacao.consultaTextoParteServico, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [textoParteServicoRequest, state]);

  useEffect(() => {
    if (document.getElementById("sqTipoParteServico")) {
      if (
        document.getElementById("sqTipoParteServico").options[
          document.getElementById("sqTipoParteServico").selectedIndex
        ].text === "Inaptidão de IE" ||
        document.getElementById("sqTipoParteServico").options[
          document.getElementById("sqTipoParteServico").selectedIndex
        ].text === "Inaptidão de IE"
      ) {
        setSelectInaptidao("Inaptidão de IE");
      } else {
        setSelectInaptidao("");
        document.getElementById("sqTipoInaptidao").selectedIndex = 0;
      }
    }

    if ((!textoParteServicoData && !textoParteServicoErrors) || textoParteServicoIsLoading) return;

    if (!formik.values.sqTextoParteServico && textoParteServicoData && textoParteServicoData.result.length) {
      const info = {
        codigo: textoParteServicoData?.result[0]?.sqTextoParteServico,
        data: {
          documentoInclusao: textoParteServicoData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: textoParteServicoData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: textoParteServicoData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: textoParteServicoData?.result[0]?.usuarioAlteracao,
          dataInclusao: textoParteServicoData?.result[0]?.dataInclusao,
          dataAlteracao: textoParteServicoData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);

      formik.setValues({ ...textoParteServicoData.result[0] });
    }

    if (textoParteServicoErrors) {
      toast.error(textoParteServicoErrors.message);
    }
  }, [formik, textoParteServicoData, textoParteServicoErrors, textoParteServicoIsLoading]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Texto de Parte de Serviço</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sqTextoParteServico
                      ? "Alteração de Texto de Parte de Serviço"
                      : "Inclusão de Texto de Parte de Serviço"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sqTextoParteServico" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={dataForm}
                errors={errorForm}
                isLoading={isLoading || textoParteServicoIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sqTextoParteServico
                      ? !isAllowed(funcionalidades.textoTipoParteServico.alterar)
                      : !isAllowed(funcionalidades.textoTipoParteServico.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Tipo de Parte de Serviço</Form.Label>
                      <SelectInputTipoParteServicoFormik
                        formik={formik}
                        field="sqTipoParteServico"
                        tipoItem="tipo-parte-servico"
                      />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Tipo de Inaptidão</Form.Label>
                      <SelectInputCachedFormik
                        formik={formik}
                        field="sqTipoInaptidao"
                        tipoItem="tipo-inaptidao"
                        disabled={selectInaptidao === ""}
                      />
                    </Form.Group>
                    <Form.Group as={Col} xs={12} md={12}>
                      <Form.Label>Texto Padrão</Form.Label>
                      <HtmlEditor formik={formik} field="txTextoPadrao" />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Texto de Parte de Serviço..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sqTextoParteServico
                            ? funcionalidades.textoTipoParteServico.alterar
                            : funcionalidades.textoTipoParteServico.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sqTextoParteServico && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormTextoParteServico;
